import { Button, SelectInput, SelectInputItem, TextField } from '@ads/components-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BANNER_STATUS_FILTER, type BannerStatusFilter } from '../../../types/bannerFilters';
import { CompanyAutocomplete } from '../CompanyAutocomplete';

/** Parâmetros aplicados ao acionar "Pesquisar" (ou Enter). */
export interface BannerSearchParams {
  readonly search: string;
  readonly status: BannerStatusFilter;
  readonly companyId: number | undefined;
}

export interface BannerFilterBarProps {
  /** Texto de busca já aplicado (usado para inicializar o campo). */
  readonly defaultValue: string;
  /** Status já aplicado (usado para inicializar o dropdown). */
  readonly defaultStatus: BannerStatusFilter;
  /** Empresa já aplicada (usada para inicializar o autocomplete). */
  readonly defaultCompanyId: number | undefined;
  /** Acionado ao pesquisar (Enter ou botão), com os filtros pendentes atuais. */
  readonly onSearch: (params: BannerSearchParams) => void;
  /** Acionado ao limpar todos os filtros (texto, status e empresa). */
  readonly onClear: () => void;
}

/**
 * Barra de filtros da listagem de Banner. Mantém texto, status
 * (Todos/Ativos/Inativos) e empresa **pendentes** localmente e só aplica ao
 * acionar "Pesquisar" (ou Enter) — trocar um dropdown não dispara busca. A
 * busca é **server-side** (`GET /api/v1/banner?title=&active=&companyId=`).
 */
export function BannerFilterBar({
  defaultValue,
  defaultStatus,
  defaultCompanyId,
  onSearch,
  onClear,
}: BannerFilterBarProps) {
  const { t } = useTranslation('banner');
  const [pendingSearch, setPendingSearch] = useState(defaultValue);
  const [pendingStatus, setPendingStatus] = useState<BannerStatusFilter>(defaultStatus);
  const [pendingCompanyId, setPendingCompanyId] = useState<number | undefined>(defaultCompanyId);
  const [pendingCompanyLabel, setPendingCompanyLabel] = useState<string>('');
  // O `Autocomplete` do ADS guarda o texto exibido em estado interno e só o
  // ressincroniza a partir de `selectedItem` quando há seleção. Zerar as props
  // daqui não apaga o que está escrito no campo — só a remontagem apaga. Por
  // isso "Limpar" troca a `key` do autocomplete.
  const [companyResetKey, setCompanyResetKey] = useState(0);

  const handleSearch = () =>
    onSearch({
      search: pendingSearch.trim(),
      status: pendingStatus,
      companyId: pendingCompanyId,
    });

  const handleClear = () => {
    setPendingSearch('');
    setPendingStatus(BANNER_STATUS_FILTER.All);
    setPendingCompanyId(undefined);
    setPendingCompanyLabel('');
    setCompanyResetKey(k => k + 1);
    onClear();
  };

  return (
    <div className="rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level1">
      <form
        className="flex flex-col items-start gap-micro"
        onSubmit={e => {
          e.preventDefault();
          handleSearch();
        }}
      >
        <div className="flex w-full flex-wrap items-end gap-mini">
          <div className="min-w-[220px] flex-[2]">
            <TextField
              label={t('banner.filters.search.label')}
              placeholder={t('banner.filters.search.placeholder')}
              value={pendingSearch}
              size="md"
              fullWidth
              leadingIcon={MagnifyingGlassIcon}
              onChange={e => setPendingSearch(e.target.value)}
            />
          </div>

          <div className="min-w-[180px] flex-1">
            <SelectInput
              label={t('banner.filters.status.label')}
              value={pendingStatus}
              onValueChange={v => setPendingStatus(v as BannerStatusFilter)}
              size="md"
              fullWidth
            >
              <SelectInputItem value={BANNER_STATUS_FILTER.All} custom={{ root: 'text-nowrap' }}>
                {t('banner.filters.status.all')}
              </SelectInputItem>
              <SelectInputItem value={BANNER_STATUS_FILTER.Active} custom={{ root: 'text-nowrap' }}>
                {t('banner.filters.status.active')}
              </SelectInputItem>
              <SelectInputItem
                value={BANNER_STATUS_FILTER.Inactive}
                custom={{ root: 'text-nowrap' }}
              >
                {t('banner.filters.status.inactive')}
              </SelectInputItem>
            </SelectInput>
          </div>

          <div className="min-w-[220px] flex-1">
            <CompanyAutocomplete
              key={companyResetKey}
              value={pendingCompanyId ?? null}
              selectedLabel={pendingCompanyLabel}
              onChange={(id, label) => {
                setPendingCompanyId(id ?? undefined);
                setPendingCompanyLabel(label);
              }}
              label={t('banner.filters.company.label')}
              placeholder={t('banner.filters.company.placeholder')}
            />
          </div>
        </div>

        <div className="flex items-center gap-nano">
          <Button type="submit" hierarchy="primary" size="md" leadingIcon={MagnifyingGlassIcon}>
            {t('banner.filters.submit')}
          </Button>
          <Button type="button" hierarchy="secondary" size="md" onClick={handleClear}>
            {t('banner.filters.clear')}
          </Button>
        </div>
      </form>
    </div>
  );
}
